import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Converts URLs in text to clickable links
 * @param text - The text that may contain URLs
 * @param iconMode - If true, replaces URLs with external link icons
 * @returns JSX with clickable links
 */
export const makeLinksClickable = (text: string, iconMode: boolean = false): React.ReactNode => {
  if (!text) return text;

  // Clean up malformed markdown links like [here](
  let cleanedText = text.replace(/\[([^\]]+)\]\(\s*$/g, '$1');
  
  // Regular expressions to match both markdown links, plain URLs, and mailto links
  // Updated to handle relative URLs (starting with /)
  const markdownLinkRegex = /\[([^\]]+)\]\(((?:https?|mailto):\/\/[^)]+|mailto:[^)]+|\/[^)]+)\)/g;
  const urlRegex = /((?:https?|mailto):\/\/[^\s\)]+|mailto:[^\s\)]+)/g;
  
  // First, handle markdown-style links
  const parts: Array<{ type: 'text' | 'markdown' | 'url', content: string, url?: string, text?: string }> = [];
  let lastIndex = 0;
  
  // Process markdown links first
  let match;
  while ((match = markdownLinkRegex.exec(cleanedText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: cleanedText.slice(lastIndex, match.index) });
    }
    
    // Add the markdown link
    parts.push({ 
      type: 'markdown', 
      content: match[0],
      text: match[1], 
      url: match[2] 
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < cleanedText.length) {
    parts.push({ type: 'text', content: cleanedText.slice(lastIndex) });
  }
  
  // If no markdown links found, treat as plain text and look for URLs
  if (parts.length === 0) {
    parts.push({ type: 'text', content: cleanedText });
  }
  
  // Process each part for plain URLs
  const finalParts: React.ReactNode[] = [];
  
  parts.forEach((part, partIndex) => {
    if (part.type === 'markdown') {
      // Render markdown link
      const isRelative = part.url!.startsWith('/');
      const isMailto = part.url!.startsWith('mailto:');
      
      if (iconMode && !isMailto && !isRelative) {
        let domain = '';
        try {
          domain = new URL(part.url!).hostname.replace('www.', '');
        } catch {
          domain = part.url!;
        }
        
        finalParts.push(
          <a
            key={`md-${partIndex}`}
            href={part.url}
            target={isRelative ? '_self' : '_blank'}
            rel={isRelative ? undefined : 'noopener noreferrer'}
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mx-1"
            title={`Visit ${domain}`}
          >
            <ExternalLink size={16} />
          </a>
        );
      } else {
        finalParts.push(
          <a
            key={`md-${partIndex}`}
            href={part.url}
            target={isRelative ? '_self' : (isMailto ? '_self' : '_blank')}
            rel={isRelative ? undefined : (isMailto ? undefined : 'noopener noreferrer')}
            className="text-blue-600 hover:text-blue-800 underline break-all"
          >
            {part.text}
          </a>
        );
      }
    } else {
      // Process plain text for URLs
      const urlParts = part.content.split(urlRegex);
      urlParts.forEach((urlPart, urlIndex) => {
        if (urlPart.match(urlRegex)) {
          const isMailto = urlPart.startsWith('mailto:');
          
          if (iconMode && !isMailto) {
            let domain = '';
            try {
              domain = new URL(urlPart).hostname.replace('www.', '');
            } catch {
              domain = urlPart;
            }
            
            finalParts.push(
              <a
                key={`url-${partIndex}-${urlIndex}`}
                href={urlPart}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mx-1"
                title={`Visit ${domain}`}
              >
                <ExternalLink size={16} />
              </a>
            );
          } else {
            finalParts.push(
              <a
                key={`url-${partIndex}-${urlIndex}`}
                href={urlPart}
                target={isMailto ? '_self' : '_blank'}
                rel={isMailto ? undefined : 'noopener noreferrer'}
                className="text-blue-600 hover:text-blue-800 underline break-all"
              >
                {isMailto ? urlPart.replace('mailto:', '') : urlPart}
              </a>
            );
          }
        } else if (urlPart) {
          finalParts.push(<span key={`text-${partIndex}-${urlIndex}`}>{urlPart}</span>);
        }
      });
    }
  });
  
  return finalParts;
};
